import { every, filter, find, flatMap, forEach, get, isEmpty, map } from 'lodash/fp';
import {
    RoutingRule,
    RoutingRuleAddParameters,
    RoutingRuleAssignment,
    RoutingRuleAssignmentAddParameters,
    RoutingRuleAssignmentDataDiscriminator,
    RoutingRuleAssignmentTargetType,
    RoutingRuleCompanyRelation,
    RoutingRuleUpdateParameters,
} from '@mews-commander/data-access';
import { SearchType } from '@mews-commander/enums';
import { RoutingRuleItemRoutingOption } from './enums';
import { RoutingRuleFormValues } from './types';

const getCompanyRelation = (searchType: SearchType) =>
    searchType === SearchType.TravelAgency
        ? RoutingRuleCompanyRelation.TravelAgency
        : RoutingRuleCompanyRelation.PartnerCompany;

export const mapFormValuesToRoutingRuleAddParameters = (values: RoutingRuleFormValues): RoutingRuleAddParameters => {
    const { company, prepayment, assignmentTargetType } = values;
    const { id: companyId, searchType } = company;
    // @ts-expect-error $TSFixMeError: Property name is missing in type. Not needed to send name, backend type will be updated.
    return {
        companyId,
        companyRelation: getCompanyRelation(searchType),
        prepayment,
        assignmentTargetType,
    };
};

export const mapFormValuesToRoutingRuleUpdateParameters = (
    values: RoutingRuleFormValues,
    routingRuleId: string
): RoutingRuleUpdateParameters => {
    const { company, prepayment, assignmentTargetType, name } = values;
    const { id: companyId, searchType } = company;

    return {
        routingRuleId,
        companyId: {
            value: companyId,
        },
        companyRelation: {
            value: getCompanyRelation(searchType),
        },
        prepayment: {
            value: prepayment,
        },
        ...(assignmentTargetType
            ? {
                  assignmentTargetType: {
                      value: assignmentTargetType,
                  },
              }
            : {}),
        name: {
            value: name,
        },
    };
};

export const mapFormValuesToRoutingRuleAssignmentAddParameters = (
    values: RoutingRuleFormValues,
    routingRuleId: string,
    serviceId: string
): Array<RoutingRuleAssignmentAddParameters> => {
    const { itemRoutingOption, productCategoryIds, itemTypes } = values;
    const assignmentProps = { serviceId, routingRuleId };
    const assignments: Array<RoutingRuleAssignmentAddParameters> = [];
    const onlySelectedButNoItemTypes =
        itemRoutingOption === RoutingRuleItemRoutingOption.OnlySelected &&
        isEmpty(itemTypes) &&
        isEmpty(productCategoryIds);

    if (itemRoutingOption === RoutingRuleItemRoutingOption.AllItems || onlySelectedButNoItemTypes) {
        assignments.push({
            ...assignmentProps,
            data: {
                discriminator: RoutingRuleAssignmentDataDiscriminator.Service,
                value: {},
            },
        });
    } else {
        forEach((productCategoryId) => {
            assignments.push({
                ...assignmentProps,
                data: {
                    discriminator: RoutingRuleAssignmentDataDiscriminator.ProductCategory,
                    value: {
                        productCategoryId,
                    },
                },
            });
        }, productCategoryIds);

        if (itemTypes && !isEmpty(itemTypes)) {
            assignments.push({
                ...assignmentProps,
                data: {
                    discriminator: RoutingRuleAssignmentDataDiscriminator.ItemTypes,
                    value: itemTypes,
                },
            });
        }
    }

    return assignments;
};

const filterAssignments = (typeDiscriminator: RoutingRuleAssignmentDataDiscriminator) =>
    filter(({ data: { discriminator } }) => discriminator === typeDiscriminator);

export const mapRoutingToFormValues = (
    routingRule: RoutingRule,
    routingRuleAssignments: Array<RoutingRuleAssignment>
): RoutingRuleFormValues => {
    const { id: routingRuleId, companyId, companyRelation, prepayment, assignmentTargetType, name } = routingRule;
    const company = {
        id: companyId,
        searchType:
            companyRelation === RoutingRuleCompanyRelation.TravelAgency ? SearchType.TravelAgency : SearchType.Company,
    };
    const isServiceRouted = every(
        ({ data: { discriminator } }) => discriminator === RoutingRuleAssignmentDataDiscriminator.Service,
        routingRuleAssignments
    );

    const itemTypesAssignments = filterAssignments(RoutingRuleAssignmentDataDiscriminator.ItemTypes)(
        routingRuleAssignments
    );
    const itemTypes = flatMap(({ data: { value } }) => value)(itemTypesAssignments);

    const productCategoryAssignments = filterAssignments(RoutingRuleAssignmentDataDiscriminator.ProductCategory)(
        routingRuleAssignments
    );
    const productCategoryIds = map(
        ({
            data: {
                value: { productCategoryId },
            },
        }) => productCategoryId
    )(productCategoryAssignments);

    const routingRuleAssignment = find({ routingRuleId })(routingRuleAssignments);
    const selectedServiceId = get('serviceId', routingRuleAssignment);

    return {
        company,
        prepayment,
        itemRoutingOption: isServiceRouted
            ? RoutingRuleItemRoutingOption.AllItems
            : RoutingRuleItemRoutingOption.OnlySelected,
        itemTypes,
        productCategoryIds,
        selectedServiceId,
        assignmentTargetType: assignmentTargetType || RoutingRuleAssignmentTargetType.CompanyAsDetails,
        name: name ?? undefined,
    };
};
